import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { API_BASE_URL } from "../../config/api";

const Address = () => {
  const [addresses, setAddresses] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [newAddress, setNewAddress] = useState({
    id: null,
    fullName: "",
    phoneNumber: "",
    addressLine1: "",
    addressLine2: "",
    landmark: "",
    city: "",
    country: "",
    postalCode: "",
    defaultAddress: false,
  });

  const [deleteConfirm, setDeleteConfirm] = useState({
    show: false,
    addressId: null,
  });

  // Get auth token from localStorage
  const getAuthToken = () => {
    try {
      // Check multiple possible token keys
      const tokenItem = localStorage.getItem('authToken') 
      
      if (!tokenItem) {
        return null;
      }
      
      // Parse the JSON token object
      const tokenObj = JSON.parse(tokenItem);
      
      // Extract the actual token value
      const token = tokenObj.value;
      
      console.log('Current token:', token);
      return token;
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  };

  // Headers for API requests
  const getHeaders = () => {
    const token = getAuthToken();
    if (!token) {
      toast.error("Authentication token not found. Please log in again.");
    }
    
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  // Fetch addresses from API
  const fetchAddresses = async () => {
    setIsLoading(true);
    try {
      const headers = getHeaders();
      console.log('Request headers:', headers);
      
      const response = await fetch(`${API_BASE_URL}/addressBook`, {
        method: 'GET',
        headers: headers
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error response:', errorData);
        throw new Error(errorData.message || 'Failed to fetch addresses');
      }

      const data = await response.json();
      console.log('Fetched addresses:', data);
      
      // Ensure each address has an id property that matches MongoDB _id
      const processedData = data.map(address => {
        // If the backend returns _id instead of id, normalize it here
        if (address._id && !address.id) {
          return { ...address, id: address._id };
        }
        return address;
      });
      
      setAddresses(processedData.sort((a, b) => b.defaultAddress - a.defaultAddress));
      setIsLoading(false);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error(error.message || "Failed to load addresses");
      setIsLoading(false);
    }
  };

  // Load addresses on component mount
  useEffect(() => {
    fetchAddresses();
  }, []);

  const handleEdit = (id) => {
    if (!id) {
      console.error("No address ID provided for edit");
      toast.error("Cannot edit address: Missing ID");
      return;
    }
    
    const addressToEdit = addresses.find((addr) => addr.id === id);
    if (addressToEdit) {
      // Make a copy to avoid direct state mutation
      setNewAddress({...addressToEdit});
      setIsEditing(true);
      setIsModalOpen(true);
    } else {
      // console.error("Address not found with ID:", id);
      toast.error("Cannot edit address: Not found");
    }
  };

  const handleInputChange = (e) => {
    const { name, checked, value, type } = e.target;
    
    // Update the newAddress state
    setNewAddress((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetForm = () => {
    setNewAddress({
      id: null,
      fullName: "",
      phoneNumber: "",
      addressLine1: "",
      addressLine2: "",
      landmark: "",
      city: "",
      country: "",
      postalCode: "",
      defaultAddress: false,
    });
    setIsEditing(false);
    setIsModalOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading(
      isEditing ? "Updating address..." : "Adding address..."
    );

    try {
      // Validate required fields
      if (!newAddress.fullName || !newAddress.phoneNumber || 
          !newAddress.addressLine1 || !newAddress.city || 
          !newAddress.country || !newAddress.postalCode) {
        throw new Error("Please fill all required fields");
      }
      
      // For editing, ensure we have a valid ID
      if (isEditing && !newAddress.id) {
        throw new Error("Cannot update address: Missing ID");
      }
      
      const url = isEditing 
        ? `${API_BASE_URL}/addressBook/${newAddress.id}` 
        : `${API_BASE_URL}/addressBook`;
      
      const method = isEditing ? 'PUT' : 'POST';
      
      console.log(`${method} request to ${url} with data:`, newAddress);
      
      const response = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(newAddress)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error response:', errorData);
        throw new Error(errorData.message || (isEditing ? 'Failed to update address' : 'Failed to add address'));
      }

      // If this is a new address and defaultAddress is true, or if we're editing and changing to default
      if (newAddress.defaultAddress) {
        let addressId = newAddress.id;
        
        // If we just created a new address, get its ID from the response
        if (!isEditing) {
          const data = await response.json();
          addressId = data.id || data._id;
        }
        
        // Set as default if we have an ID
        if (addressId) {
          await updateDefaultAddress(addressId);
        }
      }
      
      await fetchAddresses();
      
      toast.success(isEditing ? "Address updated successfully!" : "Address added successfully!", { 
        id: loadingToast 
      });
      
      resetForm();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.message, { id: loadingToast });
    }
  };

  const handleDelete = (id) => {
    if (!id) {
      console.error("No address ID provided for delete");
      toast.error("Cannot delete address: Missing ID");
      return;
    }
    
    setDeleteConfirm({
      show: true,
      addressId: id,
    });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.addressId) {
      toast.error("Cannot delete: Invalid address ID");
      setDeleteConfirm({ show: false, addressId: null });
      return;
    }
    
    const loadingToast = toast.loading("Deleting address...");

    try {
      const url = `${API_BASE_URL}/addressBook/${deleteConfirm.addressId}`;
      console.log(`DELETE request to ${url}`);
      console.log('Current API_BASE_URL:', API_BASE_URL);
      const response = await fetch(url, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error response:', errorData);
        throw new Error(errorData.message || 'Failed to delete address');
      }

      // Update the addresses state by filtering out the deleted address
      setAddresses(prevAddresses => 
        prevAddresses.filter(address => address.id !== deleteConfirm.addressId)
      );
      
      toast.success("Address deleted successfully!", {
        id: loadingToast,
      });
      
      setDeleteConfirm({ show: false, addressId: null });
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.message, { id: loadingToast });
    }
  };

  // Function to handle updating default address status
  const updateDefaultAddress = async (addressId) => {
    if (!addressId) {
      console.error("No address ID provided for setting default");
      toast.error("Cannot set default address: Missing ID");
      return;
    }
    
    const loadingToast = toast.loading("Setting default address...");
    
    try {
      // Find the address
      const addressToUpdate = addresses.find(addr => addr.id === addressId);
      if (!addressToUpdate) {
        throw new Error("Address not found");
      }
      
      console.log(`Setting address ${addressId} as default`);
      
      // Update with defaultAddress set to true
      const response = await fetch(`${API_BASE_URL}/addressBook/${addressId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          ...addressToUpdate,
          defaultAddress: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error response:', errorData);
        throw new Error(errorData.message || 'Failed to set default address');
      }

      await fetchAddresses();
      toast.success("Default address updated!", { id: loadingToast });
    } catch (error) {
      console.error('Set default error:', error);
      toast.error(error.message, { id: loadingToast });
      
      // Reset the checkbox in case of error
      setNewAddress(prev => ({
        ...prev,
        defaultAddress: false
      }));
    }
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl text-purple-600">My Addresses</h2>
        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
        >
          ADD ADDRESS
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <svg className="animate-spin h-8 w-8 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      ) : addresses.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="text-lg font-medium">No addresses found</p>
          <p className="mt-2">Add your first delivery address to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {addresses.map((address) => (
            <div
              key={address.id || address._id}
              className={`border rounded-lg transition-colors ${
                address.defaultAddress
                  ? "border-violet-300 bg-violet-50" // Changed to violet border color
                  : "border-gray-200 hover:border-violet-300" // Changed hover state to violet too
              }`}
            >
              <div className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    {address.defaultAddress ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-violet-600 bg-white px-2.5 py-1 rounded-full border border-violet-200">
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Default
                      </span>
                    ) : (
                      <button
                        onClick={() => updateDefaultAddress(address.id || address._id)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-violet-600 bg-white px-2.5 py-1 rounded-full border border-gray-200 hover:border-violet-200"
                      >
                        Set as default
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      className="p-1 text-gray-600 hover:text-violet-600 transition-colors rounded-full hover:bg-violet-50"
                      onClick={() => handleEdit(address.id || address._id)}
                      title="Edit Address"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>
                    </button>
                    <button
                      className="p-1 text-gray-600 hover:text-red-600 transition-colors rounded-full hover:bg-red-50"
                      onClick={() => handleDelete(address.id || address._id)}
                      title="Delete Address"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-gray-500 shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    <p className="font-medium text-gray-900">{address.fullName}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-gray-500 shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                    <p className="text-gray-600">{address.phoneNumber}</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg
                      className="w-4 h-4 mt-0.5 text-gray-500 shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <div className="text-gray-600">
                      <p>{address.addressLine1}</p>
                      <p>{address.addressLine2}</p>
                      {address.landmark && <p>{address.landmark}</p>}
                      <p>
                        {address.city}, {address.postalCode}
                      </p>
                      <p>{address.country}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <div className="text-center">
              <div className="mb-4 text-red-600 flex justify-center">
                <svg
                  className="w-16 h-16"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Delete Address
              </h3>

              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this address? This action cannot
                be undone.
              </p>

              <div className="flex justify-center gap-4">
                <button
                  onClick={() =>
                    setDeleteConfirm({ show: false, addressId: null })
                  }
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-3xl shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-purple-600">
                {isEditing ? "Edit Address" : "Add New Address"}
              </h3>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Full Name*
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={newAddress.fullName}
                    onChange={handleInputChange}
                    className="w-full p-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    required
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Phone Number*
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={newAddress.phoneNumber}
                    onChange={handleInputChange}
                    className="w-full p-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    required
                    placeholder="Enter phone number"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Address Line 1*
                  </label>
                  <input
                    type="text"
                    name="addressLine1"
                    value={newAddress.addressLine1}
                    onChange={handleInputChange}
                    className="w-full p-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    required
                    placeholder="House number and street name"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Address Line 2*
                  </label>
                  <input
                    type="text"
                    name="addressLine2"
                    value={newAddress.addressLine2}
                    onChange={handleInputChange}
                    className="w-full p-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    required
                    placeholder="Apartment, suite, unit, etc."
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Landmark
                    <span className="text-gray-500 text-xs ml-1">
                      (Optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    name="landmark"
                    value={newAddress.landmark}
                    onChange={handleInputChange}
                    className="w-full p-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    placeholder="Nearby landmark"
                  />
                </div>

                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    City*
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={newAddress.city}
                    onChange={handleInputChange}
                    className="w-full p-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    required
                    placeholder="Enter city"
                  />
                </div>

                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Country*
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={newAddress.country}
                    onChange={handleInputChange}
                    className="w-full p-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    required
                    placeholder="Enter country"
                  />
                </div>

                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Postal Code*
                  </label>
                  <input
                    type="text"
                    name="postalCode"
                    value={newAddress.postalCode}
                    onChange={handleInputChange}
                    className="w-full p-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    required
                    placeholder="Enter postal code"
                  />
                </div>

                <div className="col-span-1 flex items-center">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <span className="text-sm text-gray-600">
                      Set as default address
                    </span>
                    <div className="relative inline-flex items-center">
                      <input
                        type="checkbox"
                        name="defaultAddress"
                        id="defaultAddress"
                        className="sr-only peer"
                        checked={newAddress.defaultAddress}
                        onChange={handleInputChange}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
                >
                  {isEditing ? "Update Address" : "Save Address"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Address;